import { Command } from 'cmdk';
import { sanitizeHtml } from '@/utils/sanitize';
import {
  Box,
  Flex,
  FormattedDateTime,
  Icon,
  Image,
  InlineStack,
  List,
  ListItem,
  Text,
} from '@customerio/pluma-components/react';
import { PlumaBreadcrumbs as PlumaBreadcrumbsCss } from '@customerio/pluma-components/css/components';
import type { SearchResult } from '@/services/auth/types';
import { getIconNameFromSearchResult, getResultImageUrl } from './utils/result-icons';
import { getIntegrationTypeName, type IntegrationCatalog } from './utils/integration-catalog';
import { hasDescriptionField } from './utils/result-metadata';
import { isEmptyString } from '@/utils/string';

export interface SearchResultItemProps {
  item: SearchResult;
  itemKey: string;
  position: number;
  onSelect: (url: string, kind: SearchResult['kind'], position: number) => void;
  catalog: IntegrationCatalog;
}

function getSafeUrlValue(item: SearchResult): string {
  if (item.kind === 'docs') return item.url;
  const parsed = URL.canParse(item.url) ? new URL(item.url) : null;
  return parsed != null ? parsed.pathname + parsed.search : item.url;
}

function getDisplayTitle(item: SearchResult): string {
  if (item.kind !== 'navigation') return item.title;
  const segments = item.title.split(' / ');
  return segments.at(-1) ?? item.title;
}

function getBreadcrumbSegments(item: SearchResult): string[] | null {
  if (item.kind === 'action' && !isEmptyString(item.other.campaign_name)) {
    return ['Automations', item.other.campaign_name];
  }
  if (item.kind === 'navigation') {
    const segments = item.title.split(' / ');
    if (segments.length > 1) return segments.slice(0, -1);
  }
  return null;
}

function getItemDescription(item: SearchResult, catalog: IntegrationCatalog): string | undefined {
  if (hasDescriptionField(item)) {
    return item.other?.description;
  }
  return getIntegrationItemDescription(item, catalog);
}

function getIntegrationItemDescription(item: SearchResult, catalog: IntegrationCatalog): string | undefined {
  if (item.kind !== 'source' && item.kind !== 'destination') return undefined;
  const typeName = getIntegrationTypeName(item.other.type, catalog);
  // Only show type name when it differs from the connection name to avoid redundancy (e.g. "JavaScript" / "JavaScript")
  return typeName !== item.title ? typeName : undefined;
}

// Matched-text excerpt from Discovery Engine, present for any content-bearing result
// (templates, snippets, anonymous in-app). The backend omits it otherwise.
function getItemSnippet(item: SearchResult): string | undefined {
  return item.snippets?.[0];
}

function isItemArchived(item: SearchResult): boolean {
  return (item.kind === 'campaign' || item.kind === 'segment') && item.other?.archived === 'true';
}

export function SearchResultItem({ item, itemKey, position, onSelect, catalog }: SearchResultItemProps) {
  const safeUrlValue = getSafeUrlValue(item);
  const displayTitle = getDisplayTitle(item);
  const breadcrumbSegments = getBreadcrumbSegments(item);
  const description = getItemDescription(item, catalog);
  const snippet = getItemSnippet(item);
  const isArchived = isItemArchived(item);

  const integrationIconUrl = getResultImageUrl(item, catalog);
  const showCreatedAt = item.other?.created_at != null && !['source', 'destination', 'custom_form'].includes(item.kind);

  return (
    <Command.Item onSelect={() => onSelect(safeUrlValue, item.kind, position)} value={itemKey} asChild>
      <Box alignItems="flex-start" gap="050" display="flex">
        {integrationIconUrl != null ? (
          <Image mt="025" src={integrationIconUrl} size="icon-v2-sm" objectFit="contain" alt={item.title} />
        ) : (
          <Icon mt="025" name={getIconNameFromSearchResult(item)} size="sm" color="primary" />
        )}
        <Flex direction="column" gap="025" w="full">
          <Flex direction="column" w="full">
            {isArchived ? (
              <InlineStack alignBlock="center" gap="100">
                <Text text="product-p" className="fs-mask" truncate flex={1} minW="0">
                  {displayTitle}
                </Text>
                <Icon name="archive" size="sm" color="secondary" />
              </InlineStack>
            ) : (
              <Text text="product-p" className="fs-mask" truncate w="full">
                {displayTitle}
              </Text>
            )}
            {description != null ? (
              <Text size="xxs" color="secondary" className="fs-mask" truncate w="full">
                {description}
              </Text>
            ) : null}
            {/* Snippets from Discovery Engine contain <em> tags for search term highlighting */}
            {snippet != null ? (
              <Text size="xxs" color="secondary" className="fs-mask" truncate w="full">
                {/* nosemgrep: react-dangerouslysetinnerhtml — sanitized via DOMPurify in sanitizeHtml() */}
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(snippet) }} />
              </Text>
            ) : null}
          </Flex>
          {breadcrumbSegments != null ? (
            <List styleType="none" className={PlumaBreadcrumbsCss.classNames.breadcrumbsList} w="full">
              {breadcrumbSegments.map((segment, index) => (
                <ListItem
                  key={`${index}-${segment}`}
                  className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem}
                  truncate
                >
                  {segment}
                </ListItem>
              ))}
            </List>
          ) : null}
          {showCreatedAt ? (
            <Text size="xxs" color="secondary" truncate w="full">
              Created at <FormattedDateTime className="fs-mask" date={item.other?.created_at} format="medium" />
            </Text>
          ) : null}
        </Flex>
      </Box>
    </Command.Item>
  );
}
